import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { LogoutButton } from '../components/LogoutButton';
import { NotificationsBell } from '../components/NotificationsBell';
import {
  Gift, Users, Copy, Check, Mail, Shield, Hash, Eye, EyeOff,
  ChevronDown, Calendar, Sparkles, Zap,
} from 'lucide-react';
import './Profile.css';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary';

export function Profile() {
  const navigate = useNavigate();
  const { state, fetchUser, fetchReferralCode, fetchReferrals } = useApp();

  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const [showReferredUsers, setShowReferredUsers] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!state.user) await fetchUser();
      if (!state.referralCode) await fetchReferralCode();
      if (state.referrals.length === 0) await fetchReferrals();
      setLoading(false);
    };
    loadData();
  }, []);

  const copyReferralCode = () => {
    if (state.referralCode) {
      navigator.clipboard.writeText(state.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading || !state.user) {
    return (
      <div className="db-page">
        <div className="db-loading">
          <div className="spinner" />
          <p>Cargando…</p>
        </div>
      </div>
    );
  }

  const fullName = state.user.user_metadata?.full_name || 'Usuario';
  const email = state.user.email || '';
  const userId = state.user.id;

  return (
    <div className="db-page">
      {/* ============================================================
          TOPBAR
          ============================================================ */}
      <header className="sp-topbar">
        <div className="sp-topbar-left">
          <Link to="/dashboard" className="sp-topbar-brand">
            <Zap size={16} />
            <span>{APP_NAME}</span>
          </Link>
          <span className="sp-topbar-divider" aria-hidden="true" />
          <span className="sp-topbar-crumb">Perfil</span>
        </div>
        <div className="sp-topbar-right">
          <NotificationsBell />
          <button
            type="button"
            className="sp-topbar-user"
            onClick={() => navigate('/profile')}
            aria-label="Ir al perfil"
          >
            <div className="sp-topbar-avatar">
              {state.user.user_metadata?.avatar_url ? (
                <img src={state.user.user_metadata.avatar_url} alt="" />
              ) : (
                <span>{email.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <span className="sp-topbar-user-name">{fullName}</span>
          </button>
          <LogoutButton />
        </div>
      </header>

      <main className="db-main">
        {/* ============================================================
            HERO — avatar + nombre + email
            ============================================================ */}
        <section className="profile-hero">
          <div className="profile-hero-avatar">
            {state.user.user_metadata?.avatar_url ? (
              <img src={state.user.user_metadata.avatar_url} alt={fullName} />
            ) : (
              <span className="profile-hero-avatar-letter">
                {email.charAt(0).toUpperCase()}
              </span>
            )}
            <div className="profile-hero-online" aria-hidden="true" />
          </div>
          <div className="profile-hero-text">
            <span className="profile-hero-greeting">Tu cuenta</span>
            <h1 className="profile-hero-name">{fullName}</h1>
            <div className="profile-hero-meta">
              <button
                type="button"
                className="profile-email-toggle"
                onClick={() => setShowEmail((v) => !v)}
                title={showEmail ? 'Ocultar email' : 'Mostrar email'}
              >
                <Mail size={14} />
                <span className="profile-email-text">
                  {showEmail ? email : '••••••••••••@•••••.•••'}
                </span>
                {showEmail ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
              <span className="profile-meta-divider" aria-hidden="true" />
              <span className="profile-hero-since">
                <Hash size={14} /> ID {userId.slice(0, 8)}…
              </span>
            </div>
          </div>
        </section>

        {/* ============================================================
            GRID — Referidos + Cuenta
            ============================================================ */}
        <div className="sp-grid">
          {/* Programa de Referidos */}
          <section className="sp-panel sp-col-8 sp-col-sm-12">
            <div className="sp-panel-header">
              <Gift className="sp-panel-icon" />
              <h2 className="sp-panel-title">Programa de Referidos</h2>
              <span className="sp-pill sp-pill--accent">
                <Sparkles size={11} style={{ marginRight: 4 }} /> BENEFICIOS
              </span>
            </div>
            <div className="sp-panel-body">
              <p className="referral-description">
                Comparte tu código de referido y cuando un amigo se registre y compre saldo,
                ¡ambos recibirán beneficios!
              </p>

              {state.referralCode && (
                <div className="referral-code-box">
                  <span className="referral-code-label">Tu código único</span>
                  <div className="referral-code-display">
                    <code className="referral-code-text">{state.referralCode}</code>
                    <button
                      type="button"
                      className={`btn ${copied ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={copyReferralCode}
                    >
                      {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar</>}
                    </button>
                  </div>
                </div>
              )}

              <div className="referral-stats">
                <div className="referral-stat">
                  <div className="referral-stat-icon"><Users size={18} /></div>
                  <div className="referral-stat-info">
                    <span className="referral-stat-label">Usuarios referidos</span>
                    <span className="referral-stat-value">{state.referrals?.length || 0}</span>
                  </div>
                </div>
                <div className="referral-stat">
                  <div className="referral-stat-icon referral-stat-icon--accent">
                    <Sparkles size={18} />
                  </div>
                  <div className="referral-stat-info">
                    <span className="referral-stat-label">Estado</span>
                    <span className="referral-stat-value">Activo</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Información de Cuenta */}
          <section className="sp-panel sp-col-4 sp-col-sm-12">
            <div className="sp-panel-header">
              <Shield className="sp-panel-icon" />
              <h2 className="sp-panel-title">Cuenta</h2>
            </div>
            <div className="sp-panel-body">
              <div className="sp-info-list">
                <div className="sp-info-row">
                  <span className="sp-info-row-label">Usuario</span>
                  <span className="sp-info-row-value">{fullName}</span>
                </div>
                <div className="sp-info-row">
                  <span className="sp-info-row-label">Email</span>
                  <span className="sp-info-row-value">
                    {showEmail ? (
                      <code>{email}</code>
                    ) : (
                      <button
                        type="button"
                        className="sp-info-link"
                        onClick={() => setShowEmail((v) => !v)}
                      >
                        <Mail size={12} /> Mostrar
                      </button>
                    )}
                  </span>
                </div>
                <div className="sp-info-row">
                  <span className="sp-info-row-label">ID</span>
                  <span className="sp-info-row-value">
                    <code className="profile-id-code">{userId.slice(0, 8)}…</code>
                    <button
                      type="button"
                      className="sp-copy-btn"
                      onClick={() => navigator.clipboard.writeText(userId)}
                      title="Copiar ID completo"
                      aria-label="Copiar ID"
                    >
                      <Copy size={12} />
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Lista de referidos — span completo */}
          <section className="sp-panel sp-col-12">
            <div className="sp-panel-header">
              <Users className="sp-panel-icon" />
              <h2 className="sp-panel-title">Usuarios que has referido</h2>
              <span className="sp-pill sp-pill--mono">{state.referrals?.length || 0}</span>
              <div className="sp-panel-actions">
                <button
                  type="button"
                  className="sp-copy-btn"
                  onClick={() => setShowReferredUsers((v) => !v)}
                  aria-label={showReferredUsers ? 'Ocultar lista' : 'Mostrar lista'}
                  title={showReferredUsers ? 'Ocultar' : 'Mostrar'}
                >
                  <ChevronDown
                    size={16}
                    style={{ transform: showReferredUsers ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease' }}
                  />
                </button>
              </div>
            </div>
            {showReferredUsers && (
              <div className="sp-panel-body sp-panel-body--flush">
                {state.referrals && state.referrals.length > 0 ? (
                  <ul className="referral-list">
                    {state.referrals.map((referral, index) => (
                      <li key={index} className="referral-list-item">
                        <div className="referral-list-avatar">
                          <Users size={16} />
                        </div>
                        <div className="referral-list-info">
                          <span className="referral-list-name">
                            {referral.referee_name || 'Usuario'}
                          </span>
                          <span className="referral-list-date">
                            <Calendar size={11} />
                            {new Date(referral.created_at).toLocaleDateString('es-ES', {
                              day: 'numeric', month: 'long', year: 'numeric',
                            })}
                          </span>
                        </div>
                        <span className="sp-pill sp-pill--success">Activo</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="referral-empty">
                    <div className="referral-empty-icon"><Users size={40} /></div>
                    <h4>Aún no has referido a nadie</h4>
                    <p>Comparte tu código y obtén beneficios cuando tus amigos se registren.</p>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
